import { useEffect, useLayoutEffect, useMemo, useRef, useState, useId, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { useQuery } from 'react-query'
import { useControl, useMap } from 'react-map-gl'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  IconButton,
  Select,
  Slider,
  Stack,
  Switch,
  Typography,
} from '@mui/material'
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined'
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import MapLayerLegend, { mapControlIconStyle } from './MapLayerLegend'
import MapOverlayControls, { MapOverlayOption } from './MapOverlayControls'
import MapLayerOption from './MapLayerOption'
import MapZoomIndicator from './MapZoomIndicator'
import { observeMapResize } from './observeMapResize'
import MyLocationRoundedIcon from '@mui/icons-material/MyLocationRounded'
import ClimateHazardIcon from '../climateAnalytics/common/ClimateHazardIcon'
import { useClimateHazards } from '../../hooks/analytics'
import MapLayerRenderer from './MapLayerRenderer'
import {
  changeVariant,
  datasetAvailable,
  loadMapLayerCatalogue,
  MAP_LAYER_CATALOGUE_KEY,
  selectorOptions,
  toggleDataset,
  variantCaption,
} from './catalogue'
import type { MapLayerDataset, MapLayerVariant } from './catalogue'

export type { MapOverlayOption } from './MapOverlayControls'

function ControlPortal({
  position,
  rightInset = 0,
  children,
}: {
  position: 'top-right' | 'bottom-left'
  rightInset?: number
  children: ReactNode
}) {
  const control = useControl(
    () => {
      const container = document.createElement('div')
      container.className = 'mapboxgl-ctrl'
      // Stay outside Mapbox's vertical stack; reserve a rail only when present.
      container.style.position = position === 'top-right' ? 'absolute' : 'relative'
      if (position === 'top-right') {
        container.style.top = '0'
        container.style.right = `${rightInset}px`
      }
      container.style.zIndex = '2'
      return { container, onAdd: () => container, onRemove: () => container.remove() }
    },
    { position },
  )
  useLayoutEffect(() => {
    if (position === 'top-right') control.container.style.right = `${rightInset}px`
  }, [control, position, rightInset])
  return createPortal(children, control.container)
}

function DatasetControls({
  dataset,
  variant,
  onChange,
}: {
  dataset: MapLayerDataset
  variant: MapLayerVariant
  onChange: (variant: MapLayerVariant) => void
}) {
  const { current } = useMap()
  const controlId = useId()
  const minimumZoom = Math.min(
    ...variant.render.sources.flatMap(source => source.layers.map(layer => layer.minzoom || 0)),
  )
  return (
    <Stack spacing={1.5} sx={{ pt: 1, pb: 0.5 }}>
      {dataset.selectors.map(selector => {
        const options = selectorOptions(dataset, variant, selector.key)
        if (options.length < 2) return null
        const labelId = `${controlId}-${selector.key}`
        return (
          <FormControl key={selector.key} size="small" fullWidth>
            <InputLabel id={labelId}>{selector.label}</InputLabel>
            <Select
              labelId={labelId}
              label={selector.label}
              value={variant.values[selector.key] || ''}
              onChange={event => onChange(changeVariant(dataset, variant, selector.key, event.target.value))}
            >
              {options.map(value => (
                <MenuItem key={value} value={value}>
                  {selector.labels?.[value] ||
                    (selector.key === 'layer' ? dataset.variables[value]?.label : value) ||
                    value}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )
      })}
      {(dataset.selectors.length > 0 || variant.period || variant.percentile != null) && (
        <Typography variant="caption" color="text.secondary">
          {variantCaption(dataset, variant)}
        </Typography>
      )}
      {minimumZoom > 0 && (
        <Typography variant="caption" color="text.secondary">
          Local detail · visible from zoom {minimumZoom}
        </Typography>
      )}
      <Button
        size="small"
        startIcon={<MyLocationRoundedIcon />}
        sx={{ alignSelf: 'flex-start' }}
        onClick={() => current?.fitBounds(dataset.bounds, { padding: 40, duration: 700 })}
      >
        View coverage
      </Button>
    </Stack>
  )
}

export default function MapLayerControl({
  climateEnabled,
  overlays = [],
  hasNavigationControl = false,
}: {
  climateEnabled: boolean
  overlays?: MapOverlayOption[]
  hasNavigationControl?: boolean
}) {
  const rightInset = hasNavigationControl ? 40 : 0
  const [open, setOpen] = useState(false)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const panelId = useId()
  const { current } = useMap()
  const [mapSize, setMapSize] = useState({ width: 400, height: 600 })
  const [legendSpace, setLegendSpace] = useState(0)
  const [pickerTop, setPickerTop] = useState(54)
  useLayoutEffect(() => {
    if (!open || !buttonRef.current || !current) return
    setPickerTop(
      buttonRef.current.getBoundingClientRect().bottom - current.getContainer().getBoundingClientRect().top + 8,
    )
  }, [open, current, mapSize])
  useEffect(() => {
    const container = current?.getContainer()
    if (!container) return
    return observeMapResize([container], () => {
      const width = container.clientWidth
      const height = container.clientHeight
      setMapSize(previous => (previous.width === width && previous.height === height ? previous : { width, height }))
    })
  }, [current])
  useEffect(() => {
    if (!open) return
    const escape = (event: KeyboardEvent) => {
      // Select menus handle their own Escape key before it reaches this listener.
      if (event.key === 'Escape' && !event.defaultPrevented) {
        setOpen(false)
        buttonRef.current?.focus()
      }
    }
    document.addEventListener('keydown', escape)
    return () => document.removeEventListener('keydown', escape)
  }, [open])
  const [selected, setSelected] = useState<Record<string, string>>({})
  const [opacity, setOpacity] = useState(0.75)
  const requested = open || Object.keys(selected).length > 0
  const {
    data: catalogue,
    isLoading,
    error,
    refetch,
  } = useQuery(MAP_LAYER_CATALOGUE_KEY, loadMapLayerCatalogue, {
    enabled: requested,
    staleTime: Infinity,
    retry: 1,
    refetchOnWindowFocus: false,
  })
  const { data: apiHazards } = useClimateHazards({ enabled: climateEnabled && requested })
  const selections = useMemo(
    () =>
      (catalogue?.datasets || []).flatMap(dataset => {
        if (!datasetAvailable(dataset, climateEnabled)) return []
        const variant = dataset.variants.find(item => item.id === selected[dataset.id])
        return variant ? [{ dataset, variant }] : []
      }),
    [catalogue, selected, climateEnabled],
  )
  const generalDatasets = catalogue?.datasets.filter(dataset => dataset.category === 'general') || []
  const hazards = useMemo(() => {
    const labels = new Map((catalogue?.hazards || []).map(hazard => [hazard.code, hazard.label]))
    const codes = [...new Set([...apiHazards.map(hazard => hazard.name), ...labels.keys()])]
    return codes
      .map(code => ({
        code,
        label: apiHazards.find(hazard => hazard.name === code)?.display_name || labels.get(code) || code,
        datasets: catalogue?.datasets.filter(dataset => dataset.hazard_code === code) || [],
      }))
      .filter(hazard => hazard.datasets.length > 0)
  }, [catalogue, apiHazards])
  const climateSelectionCount = selections.filter(({ dataset }) => dataset.category !== 'general').length
  const count = selections.length + overlays.filter(overlay => overlay.type !== 'choice' && overlay.checked).length
  // Fit usable columns to this map, including its navigation rail and gutters.
  // A laptop dashboard card can be narrower than the viewport breakpoint.
  const availablePanelWidth = mapSize.width - rightInset - 36
  const sideBySide = availablePanelWidth >= 300 + 220
  const pickerWidth = sideBySide
    ? Math.min(380, Math.max(300, Math.floor(availablePanelWidth * 0.56)))
    : Math.min(380, Math.max(0, mapSize.width - 20 - rightInset))
  const legendWidth = sideBySide
    ? Math.min(300, availablePanelWidth - pickerWidth)
    : Math.min(300, Math.max(0, mapSize.width - 100))
  // In stacked layouts, leave room for the picker header, opacity and list.
  // Account for the legend header (38px), bottom controls (40px) and gap.
  const legendContentHeight =
    open && !sideBySide ? mapSize.height - pickerTop - 340 - 38 - 40 - 16 : mapSize.height - 160
  const legendMaxHeight = Math.min(sideBySide ? 300 : 180, Math.max(64, legendContentHeight))
  const pickerMaxHeight = mapSize.height - pickerTop - (!sideBySide && selections.length ? legendSpace + 16 : 30)
  const buttonStyle = {
    '&&': {
      width: 'auto',
      height: 36,
      px: 1.5,
      bgcolor: 'background.paper',
      color: 'text.primary',
      borderRadius: 2,
      boxShadow: 2,
      fontWeight: 700,
    },
  }

  const renderDataset = (dataset: MapLayerDataset) => {
    const variant = dataset.variants.find(item => item.id === selected[dataset.id])
    return (
      <MapLayerOption
        key={dataset.id}
        label={dataset.label}
        tags={dataset.tags}
        description={dataset.description}
        control={() => (
          <Switch
            size="small"
            checked={Boolean(variant)}
            inputProps={{ 'aria-label': `Show ${dataset.label}` }}
            onChange={event =>
              setSelected(previous => {
                return toggleDataset(catalogue?.datasets || [], previous, dataset, event.target.checked)
              })
            }
          />
        )}
      >
        {variant && (
          <DatasetControls
            dataset={dataset}
            variant={variant}
            onChange={next => setSelected(previous => ({ ...previous, [dataset.id]: next.id }))}
          />
        )}
      </MapLayerOption>
    )
  }

  return (
    <>
      {selections.length > 0 && <MapLayerRenderer selections={selections} opacity={opacity} />}
      <ControlPortal position="top-right" rightInset={rightInset}>
        <Stack direction="row" alignItems="center" gap={1}>
          <MapZoomIndicator />
          <Button
            aria-label="Map layers"
            ref={buttonRef}
            aria-expanded={open}
            aria-controls={open ? panelId : undefined}
            startIcon={<LayersOutlinedIcon />}
            sx={buttonStyle}
            onClick={() => setOpen(value => !value)}
          >
            Layers {count > 0 && <Chip label={count} size="small" color="primary" sx={{ ml: 1, height: 20 }} />}
          </Button>
        </Stack>
        {open && (
          <Paper
            id={panelId}
            role="dialog"
            aria-label="Map layers"
            aria-modal={false}
            elevation={6}
            sx={{
              position: 'absolute',
              top: 44,
              right: 0,
              width: pickerWidth,
              maxHeight: Math.max(80, pickerMaxHeight),
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              borderRadius: 3,
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ px: 2.5, py: 1.5, flexShrink: 0 }}
            >
              <Typography variant="h6" fontWeight={800}>
                Map layers
              </Typography>
              <IconButton
                aria-label="Close map layers"
                sx={mapControlIconStyle}
                onClick={() => {
                  setOpen(false)
                  buttonRef.current?.focus()
                }}
              >
                <CloseRoundedIcon />
              </IconButton>
            </Stack>
            {selections.length > 0 && (
              <Box sx={{ px: 2.5, pb: 1, flexShrink: 0 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" fontWeight={700}>
                    Dataset opacity
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {Math.round(opacity * 100)}%
                  </Typography>
                </Stack>
                <Slider
                  aria-label="Dataset opacity"
                  value={opacity}
                  min={0}
                  max={1}
                  step={0.05}
                  valueLabelDisplay="auto"
                  valueLabelFormat={value => `${Math.round(value * 100)}%`}
                  onChange={(_, value) => setOpacity(value as number)}
                />
              </Box>
            )}
            <Stack spacing={2} sx={{ px: 2.5, pb: 2.5, overflowY: 'auto', minHeight: 0 }}>
              <Stack spacing={1.5}>
                <Typography variant="overline" color="text.secondary">
                  General
                </Typography>
                <Stack spacing={1}>
                  <MapOverlayControls overlays={overlays} />
                  {generalDatasets.map(dataset => renderDataset(dataset))}
                </Stack>
              </Stack>
              {isLoading && (
                <Stack direction="row" gap={1} alignItems="center">
                  <CircularProgress size={18} />
                  <Typography variant="body2">Loading layers…</Typography>
                </Stack>
              )}
              {Boolean(error) && (
                <Alert severity="error" action={<Button onClick={() => refetch()}>Retry</Button>}>
                  Map datasets could not be loaded.
                </Alert>
              )}
              {climateEnabled && hazards.length > 0 && (
                <>
                  <Divider />
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="overline" color="text.secondary">
                      Climate hazards
                    </Typography>
                    {climateSelectionCount > 0 && (
                      <Button
                        size="small"
                        onClick={() =>
                          setSelected(previous =>
                            Object.fromEntries(
                              Object.entries(previous).filter(([id]) =>
                                generalDatasets.some(dataset => dataset.id === id),
                              ),
                            ),
                          )
                        }
                      >
                        Clear
                      </Button>
                    )}
                  </Stack>
                  {catalogue &&
                    hazards.map(hazard => (
                      <Accordion
                        key={hazard.code}
                        disableGutters
                        elevation={0}
                        defaultExpanded
                        sx={{
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: '12px !important',
                          '&:before': { display: 'none' },
                        }}
                      >
                        <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />}>
                          <Stack direction="row" gap={1} alignItems="center">
                            <ClimateHazardIcon hazardCode={hazard.code} size={30} iconSize={18} />
                            <Typography variant="subtitle2" fontWeight={800}>
                              {hazard.label}
                            </Typography>
                          </Stack>
                        </AccordionSummary>
                        <AccordionDetails sx={{ px: 1.5, pt: 0 }}>
                          <Stack spacing={1.25}>{hazard.datasets.map(dataset => renderDataset(dataset))}</Stack>
                        </AccordionDetails>
                      </Accordion>
                    ))}
                </>
              )}
            </Stack>
          </Paper>
        )}
      </ControlPortal>
      {selections.length > 0 && (
        <ControlPortal position="bottom-left">
          <MapLayerLegend
            selections={selections}
            width={legendWidth}
            maxHeight={legendMaxHeight}
            onSpaceChange={setLegendSpace}
            mapContainer={current?.getContainer()}
          />
        </ControlPortal>
      )}
    </>
  )
}
